// Iter 387 — Pestaña "No son anticipos": egresos pendientes cuya cuenta en Alegra no es de anticipo.
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Ban, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { fmtCLP, fmtRut } from './regularizacionUtils';

export function NoAnticiposTab({ lado, puedeAplicar, onCambio }) {
  const [items, setItems] = useState([]);
  const [sinVerificar, setSinVerificar] = useState(0);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [marcando, setMarcando] = useState(false);
  const [verif, setVerif] = useState({ running: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get('/contabilidad/regularizacion/no-anticipos', { params: { lado } }), api.get('/contabilidad/regularizacion/verificar-cuentas/status')]);
      setItems(r.data.items || []); setSinVerificar(r.data.sin_verificar || 0); setVerif(s.data || {}); setSel(new Set());
    } finally {
      setLoading(false);
    }
  }, [lado]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!verif.running) return undefined;
    const t = setInterval(async () => {
      const s = await api.get('/contabilidad/regularizacion/verificar-cuentas/status');
      setVerif(s.data || {});
      if (!s.data?.running) { clearInterval(t); load(); onCambio?.(); }
    }, 3000);
    return () => clearInterval(t);
  }, [verif.running, load, onCambio]);

  const porCuenta = useMemo(() => {
    const m = {};
    items.forEach((i) => { const k = i.cuenta || 'sin cuenta'; m[k] = m[k] || { n: 0, monto: 0 }; m[k].n += 1; m[k].monto += i.disponible; });
    return Object.entries(m).sort((a, b) => b[1].monto - a[1].monto);
  }, [items]);

  const verificar = async () => {
    const r = await api.post('/contabilidad/regularizacion/verificar-cuentas', { lado });
    if (r.data.started) { toast.info('Verificando cuentas en Alegra (solo lectura)…'); setVerif({ running: true }); } else toast.info('Ya hay una verificación en curso');
  };
  const marcar = async () => {
    if (!window.confirm(`Marcar ${sel.size} movimiento(s) como NO aplicables. Dejarán de aparecer como anticipos pendientes (quedan como gasto/no aplica). ¿Confirmas?`)) return;
    setMarcando(true);
    try {
      const r = await api.post('/contabilidad/regularizacion/marcar-no-aplicable', { lado, ids: [...sel] });
      toast.success(`${r.data.marcados} movimiento(s) marcados como no aplicables`);
      await load(); onCambio?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo marcar');
    } finally {
      setMarcando(false);
    }
  };
  const toggleAll = () => setSel(sel.size === items.length ? new Set() : new Set(items.map((i) => i.id)));

  return (
    <div className="space-y-3" data-testid="tab-no-anticipos">
      <div className="flex items-center gap-3 flex-wrap text-xs">
        <p className="text-muted-foreground max-w-3xl">
          Pagos pendientes cuya cuenta contable en Alegra <strong>no</strong> es de anticipo (sueldos, impuestos, retenciones, cuentas por cobrar…). No deben cruzarse con facturas:
          confírmalos como "no aplicable" para limpiar la bandeja. {sinVerificar > 0 && <span className="text-amber-700">{sinVerificar} movimiento(s) aún sin verificar su cuenta en Alegra.</span>}
        </p>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={verificar} disabled={verif.running} data-testid="btn-verificar-cuentas">
            {verif.running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {verif.running ? `Verificando ${verif.progreso || 0}/${verif.total || '?'}` : 'Verificar cuentas en Alegra'}
          </Button>
          <Button size="sm" variant="destructive" onClick={marcar} disabled={!puedeAplicar || marcando || !sel.size} data-testid="btn-marcar-no-aplicable">
            {marcando ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Ban className="w-3.5 h-3.5 mr-1" />} Marcar como no aplicable ({sel.size})
          </Button>
        </div>
      </div>
      {porCuenta.length > 0 && (
        <div className="flex flex-wrap gap-2 text-[11px]" data-testid="no-ant-por-cuenta">
          {porCuenta.map(([c, v]) => <span key={c} className="rounded-full border px-2 py-0.5 bg-slate-50" data-testid={`no-ant-chip-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>{c}: {v.n} · {fmtCLP(v.monto)}</span>)}
        </div>
      )}
      {loading ? <p className="text-sm text-muted-foreground p-6 text-center">Cargando…</p> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground p-6 text-center italic" data-testid="no-ant-vacio">Nada pendiente de confirmar.</p>
      ) : (
        <div className="border rounded overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs" data-testid="tabla-no-anticipos">
            <thead className="bg-muted sticky top-0"><tr>
              <th className="w-8 px-2"><Checkbox checked={sel.size === items.length && items.length > 0} onCheckedChange={toggleAll} data-testid="no-ant-check-all" /></th>
              <th className="text-left px-2 py-1.5">N°</th><th className="text-left px-2 py-1.5">Fecha</th><th className="text-left px-2 py-1.5">Auxiliar</th>
              <th className="text-right px-2 py-1.5">Disponible</th><th className="text-left px-2 py-1.5">Cuenta Alegra</th><th className="text-left px-2 py-1.5">Detalle</th></tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className={`border-t ${sel.has(i.id) ? 'bg-indigo-50' : ''}`} data-testid={`no-ant-row-${i.id}`}>
                  <td className="px-2 py-1"><Checkbox checked={sel.has(i.id)} onCheckedChange={() => setSel((p) => { const n = new Set(p); n.has(i.id) ? n.delete(i.id) : n.add(i.id); return n; })} data-testid={`no-ant-check-${i.id}`} /></td>
                  <td className="px-2 py-1 font-mono">{i.ref || '—'}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{fmtFecha(i.fecha)}</td>
                  <td className="px-2 py-1"><div className="truncate max-w-[220px]" title={i.nombre}>{i.nombre}</div><div className="text-[10px] text-muted-foreground font-mono">{fmtRut(i.rut)}</div></td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtCLP(i.disponible)}</td>
                  <td className="px-2 py-1 text-red-700">{i.cuenta}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[240px]" title={i.descripcion}>{i.descripcion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
